import { v } from 'h3-valibot';
import { acceptHMRUpdate, defineStore } from 'pinia';
import type {
  Props as ProductVariantPickerSelectProps,
  SelectOptions
} from '@hypercodestudio/basler-components/dist/components/modules/ProductVariantPickerSelect.vue';
import type { Props as InfoIconProps } from '@hypercodestudio/basler-components/dist/components/elements/InfoIcon.vue';
import type {
  BundleItemFragment,
  BundleOptionFragment,
  BundleProductFragment
} from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';
import { formatMoney } from '~/utils/shop/formatMoney';
import { useLogger } from '~/composables/useLogger';
import { failSafeParseJSON } from '~/utils/failSafeParseJSON';
import { createEmptyOption } from '~/stores/productDetailPageStore/helper/createEmptySelectOption';

const tooltipSchema = v.array(
  v.object({
    tooltip_text: v.object({
      tooltip_text: v.string()
    }),
    tooltip_option_uid: v.object({
      tooltip_option_uid: v.string()
    })
  })
);

function parseTooltips(tips: string) {
  return v.safeParse(tooltipSchema, failSafeParseJSON(tips));
}

export type ProductAddonsStoreState = {
  selectedOptions: Record<string, string>;
  /**
   * The current bundle product.
   *
   * Uses {@link useProductDetailPageStore}.
   */
  bundledProduct: BundleProductFragment | null;
  /**
   * The actual bundle items from the current active product.
   */
  bundleItems: ReadonlyArray<BundleItemFragment>;
  optionsMap: Record<string, BundleOptionFragment>;
};

function createOption(
  option: BundleOptionFragment,
  locale: string,
  activeId?: string
): SelectOptions {
  return {
    value: option.uid!,
    label: `${option.label ?? ''} +${formatMoney(
      option.product?.price_range?.minimum_price?.final_price,
      locale
    )}`,
    labelInfo: '', // keep this empty, because of hardcoded concatenation string '-' between label and labelinfo in the component
    selected: activeId === option.uid
  };
}

const EMPTY_ARRAY = Object.freeze([]);

/**
 * This store is responsible for logic related to product information.
 * The store relies on {@link useProductDetailPageStore}.
 */
export const useProductAddonsStore = defineStore('product-addon-store', () => {
  const selectedOptions = ref<Record<string, string>>({});
  const bundledProduct = ref<BundleProductFragment | null>(null);
  const bundleItems = ref<ReadonlyArray<BundleItemFragment>>(EMPTY_ARRAY);
  const optionsMap = ref<Record<string, BundleOptionFragment>>({});

  const _state = {
    selectedOptions,
    bundledProduct,
    bundleItems,
    optionsMap
  };

  const selectedRequiredItemUrlKey = computed<string | null | undefined>(() => {
    const firstRequiredItemUid = firstRequiredItem.value?.uid ?? '';
    const selectedOptionUid = selectedOptions.value[firstRequiredItemUid] ?? '';
    const option = optionsMap.value[selectedOptionUid];

    return option?.product?.url_key ?? bundleProductUrlKey.value;
  });

  const firstRequiredItemPeferedOption = computed<
    BundleOptionFragment | null | undefined
  >(() => {
    if (!firstRequiredItem) {
      return;
    }

    const defaultOptionProduct = firstRequiredItem.value?.options?.find(
      (option) => option?.is_default
    );

    return (
      defaultOptionProduct ??
        firstRequiredItem.value?.options?.[0]?.product?.url_key,
      // TODO: remove after all options return a product
      firstRequiredItem.value?.options?.find(
        (option) => option?.product != null
      )
    );
  });

  /**
   * Returns the product used for primary information on a bundle product.
   * This a workaround as the bundle product itself does not have
   * sufficient product information.
   */
  const bundleProductUrlKey = computed<string | null | undefined>(() => {
    return firstRequiredItemPeferedOption?.value?.product?.url_key;
  });

  const firstRequiredItem = computed<BundleItemFragment | undefined>(() => {
    return bundleItems.value.find((item) => item.required);
  });

  const tooltips = computed(() => {
    const tooltipJson = bundledProduct.value?.bundle_tooltips;
    if (!tooltipJson) {
      return;
    }

    const parsed = parseTooltips(tooltipJson);
    if (!parsed.success) {
      useLogger().warn(
        'failed to parse bundle product tooltips',
        parsed.issues
      );

      return;
    }

    // create a map with option_name -> tooltip text
    return Object.fromEntries(
      parsed.output.map((entry) => [
        entry.tooltip_option_uid.tooltip_option_uid,
        entry.tooltip_text.tooltip_text
      ])
    );
  });

  /**
   * A transformed list of items used for the according ui component.
   *
   * Uses {@link useNuxtApp#$textDictionary}.
   */

  const items = computed<ProductVariantPickerSelectProps[]>(() => {
    const { $textDictionary, $locale } = useNuxtApp();

    return bundleItems.value
      .filter((item) => item.uid !== firstRequiredItem.value?.uid)
      .map((item) => {
        const uid = item.uid!;

        const selectOptions = item.options
          ?.filter(isDefined)
          .toSorted(
            (optionA, optionB) =>
              (optionA.position ?? 0) - (optionB.position ?? 0)
          )
          .map((option) =>
            createOption(option, $locale.value, selectedOptions.value[uid])
          );
        selectOptions?.unshift(
          createEmptyOption(
            $textDictionary.value['pdp.addons.default-option.label'] ??
              'Please select an option',
            selectedOptions.value[uid] == null
          )
        );

        const tooltipText = tooltips.value?.[uid];
        const labelTooltip: InfoIconProps | undefined = tooltipText
          ? {
              tooltipText: tooltipText,
              tooltipPlacement: 'auto'
            }
          : undefined;

        const label = `${
          $textDictionary.value?.['pdp.addons.option-group.label']?.replace(
            '{attribute}',
            item.title ?? ''
          ) ?? ''
        }${item.required ? '*' : ''}`;

        return {
          id: uid,
          label,
          isActive: selectedOptions.value[uid] != null,
          isDisabled: false,
          labelTooltip,
          selectOptions
        } satisfies ProductVariantPickerSelectProps;
      });
  });

  /**
   * The price (sum) of all selected options. This does NOT include
   * the first required option (which is usually the base product).
   * Therefore, this function returns only the sum of selected addons.
   *
   * XXX: calculating prices should be done by shop.
   */

  // TODO: selectedItemsPrice
  const selectedItemsPrice = computed<number>(() => {
    return Object.values(selectedOptions.value)
      .filter(
        (optionUid) =>
          !firstRequiredItem.value?.options?.some(
            (option) => option?.uid === optionUid
          )
      )
      .map(
        (optionUid) =>
          optionsMap.value[optionUid]?.product?.price_range?.minimum_price
            ?.final_price?.value ?? 0
      )
      .reduce((collector, price) => collector + price, 0);
  });

  /**
   * A list of skus for each selected option.
   *
   * XXX: this does not include the first required option
   */

  const selectedOptionSkus = computed<ReadonlyArray<string>>(() => {
    return Object.entries(selectedOptions.value)
      .map(([itemUid, optionUid]) => {
        if (itemUid === firstRequiredItem.value?.uid) {
          return;
        }

        return optionsMap.value[optionUid]?.product?.sku;
      })
      .filter(isDefined);
  });

  /**
   * A list of required option uids.
   *
   * XXX: this does not include the first required option
   */

  const requiredOptionsUids = computed<ReadonlyArray<string>>(() => {
    return bundleItems.value
      .filter(
        (item) => firstRequiredItem.value?.uid !== item.uid && item.required
      )
      .map((item) => item.uid)
      .filter(isDefined);
  });

  /**
   * Returns true if all required options are selected.
   */
  const isRequiredOptionsSelected = computed(() => {
    // instead of .every we try to find the first empty option
    return !requiredOptionsUids.value.some(
      (uid) => selectedOptions.value[uid] == null
    );
  });

  function selectOption(itemId: string, optionId: string) {
    if (optionId === '') {
      delete selectedOptions.value[itemId];

      return;
    }

    selectedOptions.value[itemId] = optionId;
  }

  /**
   * Ensures, that the first required option has a selection.
   */
  function ensureFirstRequiredItem() {
    if (!firstRequiredItem.value || !firstRequiredItemPeferedOption.value) {
      return;
    }

    if (selectedOptions.value[firstRequiredItem.value.uid!]) {
      return;
    }

    selectedOptions.value[firstRequiredItem.value.uid!] =
      firstRequiredItemPeferedOption.value.uid!;
  }

  function setBundledProduct(product: BundleProductFragment | null) {
    bundledProduct.value = product;
    bundleItems.value =
      bundledProduct.value?.items
        ?.filter(isDefined)
        .toSorted(
          (itemA, itemB) => (itemA.position ?? 0) - (itemB.position ?? 0)
        ) ?? EMPTY_ARRAY;
    optionsMap.value = Object.fromEntries(
      bundleItems.value
        .map(
          (item) =>
            item.options
              ?.filter(isDefined)
              ?.map((option) => [option.uid!, option]) ?? EMPTY_ARRAY
        )
        .flat()
    );
  }

  return {
    _state,
    selectedOptions,
    bundledProduct,
    bundleItems,
    optionsMap,
    selectedRequiredItemUrlKey,
    firstRequiredItemPeferedOption,
    bundleProductUrlKey,
    firstRequiredItem,
    tooltips,
    items,
    selectedItemsPrice,
    selectedOptionSkus,
    requiredOptionsUids,
    isRequiredOptionsSelected,
    selectOption,
    ensureFirstRequiredItem,
    setBundledProduct
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useProductAddonsStore, import.meta.hot)
  );
}
